import React, { useState, useEffect } from "react";
import {
  Paper,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Tooltip,
  Button,
} from "@material-ui/core";
import clsx from "clsx";
import { useForm, FormProvider, Controller } from "react-hook-form";
import apiClient from "../../auth/apiClient";
import OneParkService from "../../services/OneParkService";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import LogoUpload from "./LogoUpload";
import EmailTemplateEditor from "./EmailTemplateEditor";
import useStyles from "./styles";

const oneParkService = new OneParkService(apiClient);

const RESTRICTED_SIGNUP_TOOLTIP =
  "When this setting is enabled, users will be required to enter the email and account number that is attached to their access holder to create their account. When this setting is disabled, anyone can sign up and create an account.";

const AUTO_BARCODE_TOOLTIP =
  "When this setting is enabled, users will automatically be assigned a barcode credential when their account is created.";

const OneParkAdminContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();
  const [logoData, setLogoData] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const methods = useForm({
    defaultValues: {
      inviteEmailSubject: "",
      inviteEmailBody: "",
      expiredCardEmailSubject: "",
      expiredCardEmailBody: "",
      restrictedSignup: false,
      autoGenerateBarcode: false,
    },
  });

  const { control, handleSubmit, reset, formState: { dirtyFields } } = methods;

  useEffect(() => {
    if (!facilityID) return;

    const fetchSettings = async () => {
      try {
        const response = await oneParkService.getAdminSettings(facilityID);
        const data = response.data;

        reset({
          inviteEmailSubject: data?.inviteEmailTemplate?.subject || "",
          inviteEmailBody: data?.inviteEmailTemplate?.body || "",
          expiredCardEmailSubject: data?.expiredCardEmailTemplate?.subject || "",
          expiredCardEmailBody: data?.expiredCardEmailTemplate?.body || "",
          restrictedSignup: data?.restrictedSignup || false,
          autoGenerateBarcode: data?.autoGenerateBarcodeCredential || false,
        });

      } catch {
        enqueueSnackbar("Failed to load admin settings", {
          variant: "error",
          tag: "FailedToLoadOneParkSettings",
        });
      }

      try {
        const logoResponse = await oneParkService.getLogo(facilityID);
        const logoUrl = logoResponse?.data?.url;
        if (logoUrl) {
          setLogoPreview(logoUrl);
        }
      } catch {
        // 404 means no logo uploaded yet � not an error
      }
    };

    fetchSettings();
  }, [facilityID]);

  const handleLogoChange = (logo) => {
    setLogoData(logo);
  };

  const onSubmit = async (data) => {
    try {
      const hasLogoChange = !!logoData?.file;
      const hasSettingsChange = Object.keys(dirtyFields).length > 0;

      if (!hasLogoChange && !hasSettingsChange) return;

      if (hasLogoChange) {
        const formData = new FormData();
        formData.append("file", logoData.file);
        const logoResponse = await oneParkService.uploadLogo(facilityID, formData);
        if (logoResponse?.data?.url) {
          setLogoPreview(logoResponse.data.url);
        }
      }

      const promises = [];

      if (hasSettingsChange) {
        const payload = {
          inviteEmailTemplate: { subject: data.inviteEmailSubject, body: data.inviteEmailBody },
          expiredCardEmailTemplate: { subject: data.expiredCardEmailSubject, body: data.expiredCardEmailBody },
          restrictedSignup: data.restrictedSignup,
          autoGenerateBarcodeCredential: data.autoGenerateBarcode,
        };
        promises.push(
          oneParkService.saveAdminSettings(facilityID, payload)
            .then(() => ({ type: "settings", success: true }))
            .catch(() => ({ type: "settings", success: false }))
        );
      }

      const results = await Promise.all(promises);
      const failed = results.filter((r) => !r.success).map((r) => r.type);

      if (failed.length === 0) {
        enqueueSnackbar("Settings saved successfully", {
          variant: "success",
          tag: "OneParkSettingsSaved",
        });
      } else {
        enqueueSnackbar(`Failed to save ${failed.join(" and ")}`, {
          variant: "error",
          tag: "FailedToSaveOneParkSettings",
        });
      }
    } catch {
      enqueueSnackbar("Failed to save settings", {
        variant: "error",
        tag: "FailedToSaveOneParkSettings",
      });
    }
  };

  return (
    <div className={clsx("onepark-admin-container", classes.root)}>
      <Paper className={clsx(classes.paper)}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
               <Grid item xs={12} md={3}>
                <LogoUpload
                  onLogoChange={handleLogoChange}
                  initialPreview={logoPreview}
                />
              </Grid>

              <Grid item xs={12} md={9}>
                <div className={classes.section}>
                  <EmailTemplateEditor
                    title="Invite E-Mail"
                    subjectFieldName="inviteEmailSubject"
                    bodyFieldName="inviteEmailBody"
                    control={control}
                  />
                </div>

                <div className={classes.section}>
                  <EmailTemplateEditor
                    title="Expired Card E-Mail"
                    subjectFieldName="expiredCardEmailSubject"
                    bodyFieldName="expiredCardEmailBody"
                    control={control}
                  />
                </div>
              </Grid>

              <Grid item xs={false} md={3} implementation="css" />
              <Grid item xs={12} md={9}>
                <Tooltip arrow title={RESTRICTED_SIGNUP_TOOLTIP}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="restrictedSignup"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                            data-id="restricted-signup-switch"
                            data-testid="restricted-signup-switch"
                          />
                        )}
                      />
                    }
                    label="Restricted Signup"
                    data-testid="restricted-signup-toggle"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={false} md={3} implementation="css" />
              <Grid item xs={12} md={9}>
                <Tooltip arrow title={AUTO_BARCODE_TOOLTIP}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="autoGenerateBarcode"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                            data-id="auto-barcode-switch"
                            data-testid="auto-barcode-switch"
                          />
                        )}
                      />
                    }
                    label="Auto Generate Barcode Credential"
                    data-testid="auto-barcode-toggle"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={false} md={3} implementation="css" />
              <Grid item xs={12} md={9} className={classes.actions}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="onepark-admin-save-btn"
                  data-id="save-button"
                  data-testid="save-button"
                >
                  SAVE
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Paper>
    </div>
  );
};

export default OneParkAdminContainer;
